import { useState } from "react";
import { toast } from "sonner";
import { Crown } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import useAdminLoad from "@/hooks/useAdminLoad";
import { LoadState, Panel, Pill } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import { api } from "@/lib/api";
import { TIERS } from "@/lib/constants";
import { formatDate, humanize } from "@/lib/format";

function GrantForm({ userId, onDone, timedOnly }) {
  const [tier, setTier] = useState("crushr");
  const [days, setDays] = useState(timedOnly ? "30" : "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const daysOk = (days === "" && !timedOnly) || (/^\d+$/.test(days) && +days > 0 && +days <= 3650);

  async function grant(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api(`/admin/users/${userId}/entitlements`, { body: { tier, days: days ? +days : null, reason: reason.trim() } });
      toast.success(`Granted ${tier}${days ? ` for ${days} days` : " permanently"}.`);
      setReason(""); setDays("");
      onDone();
    } catch (err) {
      setError(err.code === "grant_too_long" && err.data?.max_days ? `Support can grant up to ${err.data.max_days} days.` : err.message);
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={grant} className="mt-6 space-y-4 border-t border-border pt-6" data-testid="tier-grant-form">
      <h3 className="text-sm font-semibold">Grant tier</h3>
      <div className="grid gap-4 sm:grid-cols-[180px_1fr]">
        <div><Label htmlFor="grant-tier">Tier</Label>
          <Select value={tier} onValueChange={setTier}>
            <SelectTrigger id="grant-tier" className="mt-2" data-testid="tier-grant-select"><SelectValue /></SelectTrigger>
            <SelectContent>{TIERS.map((t) => <SelectItem key={t.key} value={t.key} data-testid={`tier-grant-option-${t.key}`}>{t.label}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label htmlFor="grant-days">Duration (days)</Label>
          <div className="mt-2 flex gap-2">
            <Input id="grant-days" inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value.replace(/\D/g, ""))} placeholder={timedOnly ? "Days" : "Blank = permanent"}
              className="max-w-[180px]" aria-invalid={!daysOk} data-testid="tier-grant-days-input" />
            <Button type="button" variant="outline" onClick={() => setDays("30")} data-testid="tier-grant-30-days-button">30 days</Button>
            {days && !timedOnly && <Button type="button" variant="ghost" onClick={() => setDays("")} data-testid="tier-grant-permanent-button">Permanent</Button>}
          </div>
          {!daysOk && <p className="cw-error mt-1 text-xs">{timedOnly ? "Enter the number of days." : "1–3650 days, or blank."}</p>}
          {timedOnly && <p className="cw-muted mt-1 text-xs" data-testid="tier-support-limit-note">Support grants are timed, up to the limit in Settings (support_grant_max_days, 30 by default).</p>}</div>
      </div>
      <div><Label htmlFor="grant-reason">Reason</Label>
        <Textarea id="grant-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why this grant? Shown in the admin log." className="mt-2" data-testid="tier-grant-reason-input" /></div>
      {error && <p className="cw-error text-sm" role="alert" data-testid="tier-grant-error">{error}</p>}
      <Button type="submit" disabled={busy || !daysOk || !reason.trim()} data-testid="tier-grant-submit-button">{busy ? "Granting…" : `Grant ${tier}${days ? ` for ${days} days` : " permanently"}`}</Button>
    </form>
  );
}

const statusOf = (e) => (e.revoked_at ? "revoked" : e.expires_at && new Date(e.expires_at) <= new Date() ? "expired" : "active");
const TONE = { active: "success", expired: "neutral", revoked: "error" };

function GrantList({ items, canEdit, onRevoke }) {
  if (!items.length) return <p className="cw-muted py-3 text-sm">No grants yet.</p>;
  return (
    <ul className="divide-y divide-border" data-testid="tier-grants-list">
      {items.map((e) => {
        const st = statusOf(e);
        return (
          <li key={e.id} className="flex flex-wrap items-center gap-3 py-3 text-sm" data-testid={`entitlement-row-${e.id}`}>
            <span className="w-16 font-medium capitalize">{e.tier}</span>
            {e.source === "launch_grant" && <Pill tone="success" data-testid={`entitlement-launch-${e.id}`}>Launch grant</Pill>}
            <Pill tone={TONE[st]} className="capitalize" data-testid={`entitlement-status-${e.id}`}>{st}</Pill>
            <span className="cw-soft min-w-0 flex-1">
              {e.label || (e.source === "launch_grant" ? "Launch grant" : humanize(e.source))}{e.granted_by ? ` by ${e.granted_by}` : ""} · {formatDate(e.created_at)} · {e.expires_at ? `until ${formatDate(e.expires_at)}` : "permanent"}
              {e.note && <span className="cw-muted block text-xs">“{e.note}”</span>}
            </span>
            {canEdit && st === "active" && <Button size="sm" variant="outline" onClick={() => onRevoke(e)} data-testid={`entitlement-revoke-button-${e.id}`}>Revoke</Button>}
          </li>
        );
      })}
    </ul>
  );
}

export default function TierPanel({ userId, role }) {
  const canEdit = role === "owner";
  const canGrant = canEdit || role === "support";
  const { data, error, loading, reload } = useAdminLoad(`/admin/users/${userId}/entitlements`);
  const [revoking, setRevoking] = useState(null);
  const s = data?.summary;
  const revoke = async () => {
    await api(`/admin/entitlements/${revoking.id}/revoke`, { method: "POST" });
    toast.success("Grant revoked.");
    reload();
  };
  return (
    <Panel className="p-6" data-testid="tier-panel">
      <LoadState loading={loading && !data} error={error} onRetry={reload}>
        <div className="flex flex-wrap items-center gap-4">
          <span className="cw-bg-tint flex h-12 w-12 items-center justify-center rounded-2xl"><Crown size={24} weight="duotone" className="cw-brand-text" aria-hidden /></span>
          <div>
            <p className="cw-muted text-xs">Effective tier</p>
            <p className="text-2xl font-semibold capitalize tracking-tight" data-testid="user-current-tier">{s?.tier || "free"}</p>
          </div>
          <div className="ml-auto text-right text-sm">
            <p className="cw-muted text-xs">Source</p>
            <p className="font-medium" data-testid="user-tier-source">{s?.tier_source ? humanize(s.tier_source) : "Default"}</p>
            <p className="cw-muted text-xs">{s?.tier_updated_at ? `since ${formatDate(s.tier_updated_at)}` : ""}</p>
          </div>
        </div>
        <h3 className="mb-1 mt-6 text-sm font-semibold">Grants</h3>
        <GrantList items={data?.all || []} canEdit={canEdit} onRevoke={setRevoking} />
        {canGrant ? <GrantForm userId={userId} onDone={reload} timedOnly={!canEdit} /> : <p className="cw-muted mt-4 text-xs" data-testid="tier-readonly-note">Owner and Support can grant tiers. Only the Owner can revoke.</p>}
      </LoadState>
      <ConfirmDialog open={!!revoking} onOpenChange={(o) => !o && setRevoking(null)} testId="revoke-confirm-dialog"
        title={`Revoke ${revoking?.tier}?`} description="Their benefits from this grant end right away." confirmLabel="Revoke" onConfirm={revoke} />
    </Panel>
  );
}
